import { Calendar, Flex } from 'antd';
import type { SelectInfo } from 'antd/es/calendar/generateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import Icon from '@ant-design/icons';
import styles from './business-hours.module.scss';
import { Button } from '../button/button';
import ArrowLeft from '@/assets/icons/angle-left-solid-full.svg?react';
import ArrowRight from '@/assets/icons/angle-right-solid-full.svg?react';
import { BusinessHoursLegend } from '../business-hours-legend/business-hours-legend';
import { formatTimeRange, getHoursForDate } from '@/utils/helpers';
import { BusinessHours } from '@/services/business-hours/business-hours.types';
import { HoursBadge } from '../hours-badge/hours-badge';

type BusinessHoursDesktopProps = {
  businessHoursData: BusinessHours | undefined;
  currentMonth: Dayjs;
  onMonthChange: (date: Dayjs) => void;
  onDateSelect: (date: Dayjs) => void;
};

export function BusinessHoursDesktop({
  businessHoursData,
  currentMonth,
  onMonthChange,
  onDateSelect,
}: BusinessHoursDesktopProps) {
  const { permanent = [], temporary = [] } = businessHoursData || {};

  const isOutsideCurrentMonth = (date: Dayjs) => {
    return date.month() !== currentMonth.month();
  };

  const renderHoursBadges = (hoursArray: typeof temporary, type: 'temporary' | 'permanent') => {
    const allClosed = hoursArray.length === 0 || hoursArray.every((period) => period.is_closed);
    if (allClosed) {
      return <HoursBadge hours="Closed" type="closed" />;
    }

    const validPeriods = hoursArray.filter(
      (period) => !period.is_closed && period.start_time && period.end_time
    );

    if (validPeriods.length === 0) {
      return <HoursBadge hours="Closed" type="closed" />;
    }

    return (
      <Flex vertical gap={2}>
        {validPeriods.map((period) => {
          const timeRange = formatTimeRange(period.start_time, period.end_time);
          return <HoursBadge key={timeRange} hours={timeRange || 'Closed'} type={type} />;
        })}
      </Flex>
    );
  };

  const dateCellRender = (value: Dayjs) => {
    if (isOutsideCurrentMonth(value)) {
      return null;
    }

    const hoursArray = getHoursForDate(temporary, permanent, value);
    const type =
      hoursArray.length > 0 && hoursArray[0].status === 'temporary' ? 'temporary' : 'permanent';

    return renderHoursBadges(hoursArray, type);
  };

  const handleDateSelect = (date: Dayjs, selectInfo: SelectInfo) => {
    if (selectInfo.source === 'date') {
      onDateSelect(date);
    }
  };

  return (
    <Flex vertical>
      <BusinessHoursLegend />
      <Calendar
        disabledDate={(currentDate) =>
          currentDate.isBefore(dayjs(), 'day') || isOutsideCurrentMonth(currentDate)
        }
        mode="month"
        value={currentMonth}
        cellRender={(date, info) => (info.type === 'date' ? dateCellRender(date) : null)}
        onSelect={(date, selectInfo) => handleDateSelect(date, selectInfo)}
        headerRender={() => (
          <Flex justify="space-between" align="center" className={styles.calendarHeader}>
            <Button
              buttonVariant="secondary"
              type="text"
              onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
            >
              <Icon
                component={() => (
                  <ArrowLeft className={styles.navArrowButtonIcon} width="20" height="20" />
                )}
              />
            </Button>

            <span className={styles.calendarHeaderTitle}>{currentMonth.format('MMMM YYYY')}</span>

            <Flex gap={8} align="center">
              <Button buttonVariant="secondary" size="small" onClick={() => onMonthChange(dayjs())}>
                Today
              </Button>
              <Button
                buttonVariant="secondary"
                type="text"
                onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
              >
                <Icon
                  component={() => (
                    <ArrowRight className={styles.navArrowButtonIcon} width="20" height="20" />
                  )}
                />
              </Button>
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
